import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface TermsOfServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Language = "en" | "fr";

const translations = {
  en: {
    title: "Terms of Service (ToS)",
    subtitle: "PadELO - ELO Rating System for Padel",
    sections: {
      acceptance: {
        title: "1. Acceptance of Terms",
        content: "By creating an account on PadELO, you unreservedly accept these Terms of Service (ToS). If you do not accept these terms, you must not use our service."
      },
      accountCreation: {
        title: "2. Account Creation",
        eligibility: {
          title: "2.1 Eligibility Requirements",
          items: [
            "You must be at least 13 years old to create an account",
            "You must provide accurate and up-to-date information",
            "Only one account per individual is allowed",
            "You must use your real and verifiable identity"
          ]
        }
      },
      serviceUsage: {
        title: "3. Service Usage",
        legitimate: {
          title: "3.1 Legitimate Use",
          content: "You agree to use PadELO only for legitimate purposes consistent with the spirit of the ELO rating system."
        },
        prohibitions: {
          title: "3.2 Strict Prohibitions",
          fraudTitle: "FRAUD AND ELO SYSTEM MANIPULATION",
          intro: "Creating fake accounts, multiple accounts, or any attempt to manipulate the ELO rating system is <strong>STRICTLY PROHIBITED</strong> and constitutes <strong>FRAUD</strong>.",
          listTitle: "The following practices are explicitly prohibited:",
          items: [
            "<strong>Multiple account creation</strong>: Creating multiple accounts for the same individual",
            "<strong>Fake accounts</strong>: Creating accounts with false identities or information",
            "<strong>ELO manipulation</strong>: Organizing fictitious or rigged matches to artificially modify ELO rankings",
            "<strong>Collusion</strong>: Conspiring with other players to manipulate results",
            "<strong>Ghost accounts</strong>: Creating inactive or fictitious accounts to influence statistics",
            "<strong>Identity theft</strong>: Using another person's identity"
          ]
        }
      },
      sanctions: {
        title: "4. Sanctions in Case of Fraud",
        intro: "In case of detected fraud, ELO system manipulation, or violation of these terms, PadELO reserves the right to apply the following sanctions <strong>IMMEDIATELY</strong>, without notice:",
        automatic: {
          title: "4.1 Automatic Sanctions",
          eloReset: {
            title: "<strong>ELO Reset</strong>",
            items: [
              "The ELO of all affected accounts will be reset to the starting value (1500 points)",
              "All statistics related to fraudulent matches will be deleted"
            ]
          },
          ban: {
            title: "<strong>Ban</strong>",
            items: [
              "<strong>Temporary ban</strong>: Service access suspension for a minimum duration of <strong>3 months</strong>",
              "<strong>Permanent ban</strong>: In case of repeat offense or serious fraud, permanent ban"
            ]
          },
          accountDeletion: {
            title: "<strong>Account Deletion</strong>",
            items: [
              "<strong>Immediate deletion</strong> of all accounts linked to the same individual",
              "Deletion of all accounts identified as part of the fraud system",
              "No refund will be issued in case of account deletion"
            ]
          }
        }
      },
      intellectualProperty: {
        title: "5. Intellectual Property",
        content: "All content, logos, and features of PadELO are protected by intellectual property laws. You may not copy, modify, or distribute these elements without authorization."
      },
      dataProtection: {
        title: "6. Data Protection",
        content: "Your personal data is processed in accordance with our Privacy Policy. By creating an account, you accept the processing of your data for the needs of the service."
      },
      liability: {
        title: "7. Limitation of Liability",
        content: "PadELO cannot be held responsible for direct or indirect damages resulting from the use or inability to use the service."
      },
      modification: {
        title: "8. Modification of ToS",
        content: "PadELO reserves the right to modify these ToS at any time. Users will be informed of important changes. Continued use of the service after modification constitutes acceptance of the new terms."
      },
      termination: {
        title: "9. Termination",
        content: "PadELO reserves the right to suspend or terminate your account at any time in case of violation of these ToS, without notice or refund."
      },
      applicableLaw: {
        title: "10. Applicable Law",
        content: "These ToS are governed by French law. Any dispute will be submitted to the competent courts."
      },
      warning: {
        title: "⚠️ WARNING",
        content: "By checking the ToS acceptance box when creating your account, you acknowledge having read, understood and accepted all of these conditions, particularly the sanctions in case of fraud and ELO system manipulation.",
        emphasis: "Fraud and ELO system manipulation are serious offenses that result in immediate and severe sanctions."
      }
    }
  },
  fr: {
    title: "Conditions Générales d'Utilisation (CGU)",
    subtitle: "PadELO - Système de Classement ELO pour le Padel",
    sections: {
      acceptance: {
        title: "1. Acceptation des Conditions",
        content: "En créant un compte sur PadELO, vous acceptez sans réserve les présentes Conditions Générales d'Utilisation (CGU). Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser notre service."
      },
      accountCreation: {
        title: "2. Création de Compte",
        eligibility: {
          title: "2.1 Conditions d'Éligibilité",
          items: [
            "Vous devez être âgé d'au moins 13 ans pour créer un compte",
            "Vous devez fournir des informations exactes et à jour",
            "Un seul compte par personne physique est autorisé",
            "Vous devez utiliser votre identité réelle et vérifiable"
          ]
        }
      },
      serviceUsage: {
        title: "3. Utilisation du Service",
        legitimate: {
          title: "3.1 Utilisation Légitime",
          content: "Vous vous engagez à utiliser PadELO uniquement à des fins légitimes et conformes à l'esprit du système de classement ELO."
        },
        prohibitions: {
          title: "3.2 Interdictions Strictes",
          fraudTitle: "FRAUDE ET MANIPULATION DU SYSTÈME ELO",
          intro: "La création de faux comptes, de comptes multiples, ou toute tentative de manipulation du système de classement ELO est <strong>STRICTEMENT INTERDITE</strong> et constitue une <strong>FRAUDE</strong>.",
          listTitle: "Les pratiques suivantes sont explicitement interdites :",
          items: [
            "<strong>Création de comptes multiples</strong> : Créer plusieurs comptes pour une même personne physique",
            "<strong>Faux comptes</strong> : Créer des comptes avec de fausses identités ou informations",
            "<strong>Manipulation de l'ELO</strong> : Organiser des matchs fictifs ou truqués pour modifier artificiellement les classements ELO",
            "<strong>Collusion</strong> : S'entendre avec d'autres joueurs pour manipuler les résultats",
            "<strong>Comptes fantômes</strong> : Créer des comptes inactifs ou fictifs pour influencer les statistiques",
            "<strong>Usurpation d'identité</strong> : Utiliser l'identité d'une autre personne"
          ]
        }
      },
      sanctions: {
        title: "4. Sanctions en Cas de Fraude",
        intro: "En cas de détection de fraude, de manipulation du système ELO, ou de violation des présentes conditions, PadELO se réserve le droit d'appliquer <strong>IMMÉDIATEMENT</strong> les sanctions suivantes, sans préavis :",
        automatic: {
          title: "4.1 Sanctions Automatiques",
          eloReset: {
            title: "<strong>Réinitialisation de l'ELO</strong>",
            items: [
              "L'ELO de tous les comptes concernés sera réinitialisé à la valeur de départ (1500 points)",
              "Toutes les statistiques liées aux matchs frauduleux seront supprimées"
            ]
          },
          ban: {
            title: "<strong>Bannissement</strong>",
            items: [
              "<strong>Bannissement temporaire</strong> : Suspension de l'accès au service pour une durée minimale de <strong>3 mois</strong>",
              "<strong>Bannissement permanent</strong> : En cas de récidive ou de fraude grave, bannissement définitif"
            ]
          },
          accountDeletion: {
            title: "<strong>Suppression des Comptes</strong>",
            items: [
              "<strong>Suppression immédiate</strong> de tous les comptes liés à la même personne physique",
              "Suppression de tous les comptes identifiés comme faisant partie du système de fraude",
              "Aucun remboursement ne sera effectué en cas de suppression de compte"
            ]
          }
        }
      },
      intellectualProperty: {
        title: "5. Propriété Intellectuelle",
        content: "Tous les contenus, logos, et fonctionnalités de PadELO sont protégés par les lois sur la propriété intellectuelle. Vous ne pouvez pas copier, modifier, ou distribuer ces éléments sans autorisation."
      },
      dataProtection: {
        title: "6. Protection des Données",
        content: "Vos données personnelles sont traitées conformément à notre Politique de Confidentialité. En créant un compte, vous acceptez le traitement de vos données pour les besoins du service."
      },
      liability: {
        title: "7. Limitation de Responsabilité",
        content: "PadELO ne peut être tenu responsable des dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le service."
      },
      modification: {
        title: "8. Modification des CGU",
        content: "PadELO se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés des modifications importantes. La poursuite de l'utilisation du service après modification vaut acceptation des nouvelles conditions."
      },
      termination: {
        title: "9. Résiliation",
        content: "PadELO se réserve le droit de suspendre ou résilier votre compte à tout moment en cas de violation des présentes CGU, sans préavis ni remboursement."
      },
      applicableLaw: {
        title: "10. Droit Applicable",
        content: "Les présentes CGU sont régies par le droit français. Tout litige sera soumis aux tribunaux compétents."
      },
      warning: {
        title: "⚠️ ATTENTION",
        content: "En cochant la case d'acceptation des CGU lors de la création de votre compte, vous reconnaissez avoir lu, compris et accepté l'ensemble de ces conditions, notamment les sanctions en cas de fraude et de manipulation du système ELO.",
        emphasis: "La fraude et la manipulation du système ELO sont des infractions graves qui entraînent des sanctions immédiates et sévères."
      }
    }
  }
};

export default function TermsOfServiceDialog({ open, onOpenChange }: TermsOfServiceDialogProps) {
  const [language, setLanguage] = useState<Language>("en");
  const t = translations[language];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>{t.title}</DialogTitle>
              <DialogDescription>
                {t.subtitle}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="language-switch" className="text-sm font-normal cursor-pointer flex items-center gap-2">
                <span className={language === "en" ? "opacity-100" : "opacity-50"}>🇬🇧</span>
                <span className={language === "fr" ? "opacity-100" : "opacity-50"}>🇫🇷</span>
              </Label>
              <Switch
                id="language-switch"
                checked={language === "fr"}
                onCheckedChange={(checked) => setLanguage(checked ? "fr" : "en")}
              />
            </div>
          </div>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4 text-sm">
            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.acceptance.title}</h3>
              <p className="text-muted-foreground">
                {t.sections.acceptance.content}
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.accountCreation.title}</h3>
              <div className="space-y-2 text-muted-foreground">
                <div>
                  <h4 className="font-medium mb-1">{t.sections.accountCreation.eligibility.title}</h4>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    {t.sections.accountCreation.eligibility.items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.serviceUsage.title}</h3>
              <div className="space-y-2 text-muted-foreground">
                <div>
                  <h4 className="font-medium mb-1">{t.sections.serviceUsage.legitimate.title}</h4>
                  <p>{t.sections.serviceUsage.legitimate.content}</p>
                </div>
                <div>
                  <h4 className="font-medium mb-1 text-destructive">{t.sections.serviceUsage.prohibitions.title}</h4>
                  <p className="font-semibold text-foreground mb-2">{t.sections.serviceUsage.prohibitions.fraudTitle}</p>
                  <p className="mb-2" dangerouslySetInnerHTML={{ __html: t.sections.serviceUsage.prohibitions.intro }} />
                  <p className="mb-2">{t.sections.serviceUsage.prohibitions.listTitle}</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    {t.sections.serviceUsage.prohibitions.items.map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2 text-destructive">{t.sections.sanctions.title}</h3>
              <p className="text-muted-foreground mb-2" dangerouslySetInnerHTML={{ __html: t.sections.sanctions.intro }} />
              <div className="space-y-3">
                <div className="border-l-4 border-destructive pl-3">
                  <h4 className="font-medium mb-1">{t.sections.sanctions.automatic.title}</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-2">
                    <li>
                      <div dangerouslySetInnerHTML={{ __html: t.sections.sanctions.automatic.eloReset.title }} />
                      <ul className="list-disc list-inside ml-4 mt-1">
                        {t.sections.sanctions.automatic.eloReset.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </li>
                    <li>
                      <div dangerouslySetInnerHTML={{ __html: t.sections.sanctions.automatic.ban.title }} />
                      <ul className="list-disc list-inside ml-4 mt-1">
                        {t.sections.sanctions.automatic.ban.items.map((item, index) => (
                          <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                        ))}
                      </ul>
                    </li>
                    <li>
                      <div dangerouslySetInnerHTML={{ __html: t.sections.sanctions.automatic.accountDeletion.title }} />
                      <ul className="list-disc list-inside ml-4 mt-1">
                        {t.sections.sanctions.automatic.accountDeletion.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </li>
                  </ol>
                </div>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.intellectualProperty.title}</h3>
              <p className="text-muted-foreground">
                {t.sections.intellectualProperty.content}
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.dataProtection.title}</h3>
              <p className="text-muted-foreground">
                {t.sections.dataProtection.content}
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.liability.title}</h3>
              <p className="text-muted-foreground">
                {t.sections.liability.content}
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.modification.title}</h3>
              <p className="text-muted-foreground">
                {t.sections.modification.content}
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.termination.title}</h3>
              <p className="text-muted-foreground">
                {t.sections.termination.content}
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">{t.sections.applicableLaw.title}</h3>
              <p className="text-muted-foreground">
                {t.sections.applicableLaw.content}
              </p>
            </section>

            <section className="border-t pt-4 mt-4">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="font-semibold text-destructive mb-2">{t.sections.warning.title}</p>
                <p className="text-sm text-muted-foreground">
                  {t.sections.warning.content}
                </p>
                <p className="text-sm font-semibold text-destructive mt-2">
                  {t.sections.warning.emphasis}
                </p>
              </div>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

